// Suggestions of the header search: cars, posts and categories from
// adream-catalog/v1/search (Typesense) while the visitor types.

const { __, sprintf } = wp.i18n;
const DOMAIN = "frontenda-store-front";
const MIN_LENGTH = 2;
const DELAY = 200;

const money = new Intl.NumberFormat("uk-UA", { maximumFractionDigits: 0 });

function escapeHtml(value) {
	const div = document.createElement("div");
	div.textContent = value ?? "";
	return div.innerHTML;
}

function lotPrice(lot) {
	if (lot.source === "auto-in-ukraine") {
		return lot.price ? `$${money.format(lot.price)}` : "";
	}
	if (lot.ua_price) {
		return sprintf(__("$%s в Україні", DOMAIN), money.format(lot.ua_price));
	}
	if (lot.price) {
		return sprintf(__("Купити зараз $%s", DOMAIN), money.format(lot.price));
	}
	return lot.current_bid ? sprintf(__("Ставка $%s", DOMAIN), money.format(lot.current_bid)) : "";
}

function lotItem(lot) {
	const details = [
		lot.source === "auto-in-ukraine" ? __("В Україні", DOMAIN) : __("Аукціон США", DOMAIN),
		lot.odometer ? `${money.format(lot.odometer)} ${__("км", DOMAIN)}` : "",
	].filter(Boolean);
	const price = escapeHtml(lotPrice(lot));
	const thumb = lot.thumb
		? `<img src="${escapeHtml(lot.thumb)}" alt="" width="64" height="48" loading="lazy" class="h-12 w-16 flex-none rounded object-cover" />`
		: `<span class="h-12 w-16 flex-none rounded bg-theme-darkgrey"></span>`;

	return `<a href="${escapeHtml(lot.url)}" class="flex items-center gap-3 rounded p-2 text-white hover:bg-white/10 hover:text-white focus:bg-white/10 outline-none" data-site-search-item>
		${thumb}
		<span class="min-w-0 flex-1">
			<span class="block truncate">${escapeHtml(lot.title)}</span>
			<span class="block truncate text-sm text-[#828282]">${escapeHtml(details.join(" · "))}</span>
			<span class="block text-sm text-[#FFC33B] sm:hidden">${price}</span>
		</span>
		<span class="hidden flex-none text-sm text-[#FFC33B] sm:block">${price}</span>
	</a>`;
}

function postItem(post) {
	// The snippet is HTML from the server: escaped text with <mark> around matches.
	const text = post.snippet || escapeHtml(post.excerpt);
	return `<a href="${escapeHtml(post.url)}" class="block rounded p-2 text-white hover:bg-white/10 hover:text-white focus:bg-white/10 outline-none [&_mark]:bg-transparent [&_mark]:text-[#FFC33B]" data-site-search-item>
		<span class="block truncate">${escapeHtml(post.title)}</span>
		${text ? `<span class="block text-sm text-[#BDBDBD] line-clamp-2">${text}</span>` : ""}
	</a>`;
}

function categoryItem(category) {
	return `<a href="${escapeHtml(category.url)}" class="inline-block rounded border border-[#828282] px-3 py-1 text-white hover:border-[#FFC33B] hover:text-[#FFC33B] focus:border-[#FFC33B] outline-none" data-site-search-item>
		${escapeHtml(category.title)}
	</a>`;
}

function group(title, found, items, render, layout = "flex flex-col") {
	if (!items.length) {
		return "";
	}
	// Not <section>: the theme pads every section.
	return `<div class="mb-3">
		<h3 class="mb-1 px-2 text-xs uppercase tracking-wider text-[#828282]">${escapeHtml(title)} <span>${found}</span></h3>
		<div class="${layout}">${items.map(render).join("")}</div>
	</div>`;
}

function render(data, query, searchUrl) {
	const found = data.lots.found + data.posts.found + data.categories.found;
	if (!found) {
		return `<p class="p-2 text-[#BDBDBD]">${escapeHtml(sprintf(__("За запитом «%s» нічого не знайдено.", DOMAIN), query))}</p>`;
	}

	return (
		group(__("Категорії", DOMAIN), data.categories.found, data.categories.hits, categoryItem, "flex flex-wrap gap-2 px-2") +
		group(__("Авто", DOMAIN), data.lots.found, data.lots.hits, lotItem) +
		group(__("Статті", DOMAIN), data.posts.found, data.posts.hits, postItem) +
		`<a href="${escapeHtml(searchUrl)}" class="block rounded p-2 text-center text-[#FFC33B] hover:bg-white/10 hover:text-[#FFC33B] focus:bg-white/10 outline-none" data-site-search-item>
			${escapeHtml(__("Усі результати", DOMAIN))}
		</a>`
	);
}

export default function siteSearch() {
	const panel = document.querySelector("[data-site-search]");
	const toggle = document.querySelector("[data-site-search-toggle]");
	if (!panel || !toggle) {
		return;
	}

	const input = panel.querySelector("[data-site-search-input]");
	const results = panel.querySelector("[data-site-search-results]");
	const form = input.form;
	let timer = 0;
	let controller = null;

	const setOpen = (open) => {
		panel.classList.toggle("open", open);
		toggle.setAttribute("aria-expanded", String(open));
		if (open) {
			input.focus();
			input.select();
		}
	};

	const search = async () => {
		const query = input.value.trim();
		controller?.abort();
		if (query.length < MIN_LENGTH) {
			results.innerHTML = "";
			return;
		}

		controller = new AbortController();
		const url = new URL(panel.dataset.endpoint);
		url.searchParams.set("q", query);
		try {
			const response = await fetch(url, { signal: controller.signal });
			const data = await response.json();
			const searchUrl = new URL(form.action);
			searchUrl.searchParams.set("s", query);
			results.innerHTML = render(data, query, searchUrl.toString());
		} catch (error) {
			if (error.name !== "AbortError") {
				results.innerHTML = "";
			}
		}
	};

	toggle.addEventListener("click", () => setOpen(!panel.classList.contains("open")));

	input.addEventListener("input", () => {
		clearTimeout(timer);
		timer = setTimeout(search, DELAY);
	});

	// Arrow keys move between the input and the suggestions; Escape closes.
	panel.addEventListener("keydown", (event) => {
		if (event.key === "Escape") {
			setOpen(false);
			toggle.focus();
			return;
		}
		if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
			return;
		}
		const items = [input, ...results.querySelectorAll("[data-site-search-item]")];
		const index = items.indexOf(document.activeElement);
		if (index === -1) {
			return;
		}
		event.preventDefault();
		const next = index + (event.key === "ArrowDown" ? 1 : -1);
		items[(next + items.length) % items.length].focus();
	});

	document.addEventListener("click", (event) => {
		if (panel.classList.contains("open") && !panel.contains(event.target) && !toggle.contains(event.target)) {
			setOpen(false);
		}
	});
}
